<template>
  <v-container fluid class="rbu-edition-detail" v-if="edition">
    <v-row class="flex-grow-0">
      <v-btn text color="primary" exact :to="{ name: 'rbu-edition-list' }">
        <h3 class="font-weight-regular">&lt;&lt; {{ $t("app_drawer.rbu") }}</h3>
      </v-btn>
    </v-row>

    <v-row class="rbu-edition-info-wrapper" style="margin-bottom: 0.25rem">
      <h3>{{ edition.edition }}</h3>
      <v-btn
        v-if="canCreateEdition"
        color="primary"
        class="ml-4"
        small
        @click="createVolume"
      >
        <v-icon>mdi-plus</v-icon>
        {{ $t("new") }}
      </v-btn>
      <v-btn
        color="primary"
        class="ml-4"
        small
        @click="importFromSor"
        v-if="!edition.imported_sor && !edition.importing_sor_into_rbu"
      >
        <v-icon>mdi-import</v-icon>
        {{
          edition.type === "CORRIGENDUM"
            ? "Import From SoR And RBU"
            : $t("rbu.import_from_sor")
        }}
      </v-btn>
    </v-row>
    <v-row class="flex-grow-0 py-2" v-if="edition.imported_sor">
      <h5>Imported SoR: {{ edition.imported_sor.name }}</h5>
    </v-row>

    <v-row class="flex-grow-1 rounded-b">
      <div
        v-if="edition && edition.importing_sor_into_rbu == true"
        style="
          display: flex;
          flex-direction: column;
          width: 100%;
          justify-content: center;
          align-items: center;
        "
      >
        {{ edition.importing_sor_into_log }}
        <v-progress-linear v-model="import_progress" height="25">
          <strong style="color: white">
            {{ Math.ceil(import_progress) }}%</strong
          >
        </v-progress-linear>
      </div>
      <RbuVolumeList
        v-else
        full-height
        :extra-filters="{ edition_id: [$route.params.editionId] }"
        :refresh-trigger.sync="refreshTrigger"
      ></RbuVolumeList>
    </v-row>
  </v-container>
</template>
<script>
import { ACTION_TYPES } from "@/store/types";
import { computed, onMounted, onUnmounted, ref } from "@vue/composition-api";
import RbuSectionList from "@/components/rbu/RbuSectionList.vue";
import RbuVolumeList from "@/components/rbu/RbuVolumeList.vue";
import CreateRbuVolumeDialog from "@/components/rbu/CreateRbuVolumeDialog.vue";
import RbuImportSorAndRbuDialog from "@/components/rbu/RbuImportSorAndRbuDialog.vue";
import RbuImportSorDialog from "@/components/rbu/RbuImportSorDialog.vue";
import SectionList from "@/components/section/SectionList.vue";
import { PERMISSIONS, USER_TYPE } from "@/constants";

export default {
  name: "RbuEdition",
  components: { SectionList, RbuVolumeList, RbuSectionList },
  setup(props, { root }) {
    let refreshInterval = null;
    const edition = ref(null);
    const canCreateEdition = computed(() => {
      if (!edition.value) return false;
      if (!root.$store.getters.account) return false;
      return (
        !edition.value.importing_sor_into_rbu &&
        [USER_TYPE.SUPER_ADMIN, USER_TYPE.ADMIN].includes(
          root.$store.getters.account.type
        )
      );
    });
    const import_progress = computed(() => {
      if (
        edition.value &&
        edition.value.importing_sor_into_rbu &&
        edition.value.importing_sor_into_log
      ) {
        const values = edition.value.importing_sor_into_log.split("/");
        if ((values.length === 2 || values.length === 3) && values[1] !== "0") {
          const v = values[0] / values[1];
          return v * 100;
        }
        return 0;
      } else {
        return 0;
      }
    });

    const import_progress_text = computed(() => {
      if (
        edition.value &&
        edition.value.importing_sor_into_rbu &&
        edition.value.importing_sor_into_log
      ) {
        const values = edition.value.importing_sor_into_log.split("/");
        if (values.length == 3) {
          return values[2];
        }
        return "";
      } else {
        return "";
      }
    });
    const getEdition = async function (not_blocking = true, force = false) {
      if (
        edition.value == null ||
        edition.value.importing_sor_into_rbu ||
        force
      ) {
        const response = await root.$store.dispatch(ACTION_TYPES.CALL_API, {
          url: "admin/rbu/edition/",
          params: { edition_id: root.$route.params.editionId },
          opt: { not_blocking },
        });

        if (response.body.success) {
          edition.value = response.body.edition;
        }
      }
    };

    const refreshTrigger = ref(false);

    const createVolume = async function () {
      const volumeId = await root.$dialog.showAndWait(CreateRbuVolumeDialog, {
        showClose: false,
        persistent: true,
        editionId: root.$route.params.editionId,
      });

      if (volumeId) {
        root.$router.push({ name: "rbu-volume-detail", params: { volumeId } });
      }
    };

    const importFromSor = async function () {
      if (edition.value && edition.value.type === "NEW_EDITION") {
        const needRefresh = await root.$dialog.showAndWait(RbuImportSorDialog, {
          showClose: false,
          persistent: true,
          editionId: root.$route.params.editionId,
          width: 500,
        });

        getEdition(true, true);
      } else if (edition.value && edition.value.type === "CORRIGENDUM") {
        const needRefresh = await root.$dialog.showAndWait(
          RbuImportSorAndRbuDialog,
          {
            showClose: false,
            persistent: true,
            editionId: root.$route.params.editionId,
            width: 500,
          }
        );

        getEdition(true, true);
      }

      // if (needRefresh) {
      //   refreshTrigger.value = true;
      // }
    };

    onMounted(() => {
      getEdition();
      refreshInterval = setInterval(getEdition, 2500);
    });

    onUnmounted(() => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    });

    return {
      edition,
      refreshTrigger,
      createVolume,
      importFromSor,
      import_progress,
      canCreateEdition,
      import_progress_text,
    };
  },
};
</script>

<style lang="less">
.rbu-edition-detail {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;

  .rbu-edition-info-wrapper {
    flex: 0 0 auto;
    display: flex;

    .rbu-edition-info {
      flex: 1 1 0;
    }
  }
}
</style>
